/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Upload,
  Edit2,
  Trash2,
  RefreshCw,
  FileCode,
  Download,
  Crown,
  Lock,
  MoreVertical,
  Eye,
  EyeOff,
  Star,
} from 'lucide-react';
import {
  useTemplateCatalog,
  useDeleteTemplate,
  useRegeneratePreview,
  useUpdateTemplateMetadata,
} from '@/hooks/api/useTemplateCatalog';
import { getPreviewUrl, getDownloadJrxmlUrl } from '@/services/superadmin/template-catalog.service';
import { useNotification } from '@/hooks/useNotification';
import { TemplateCatalogItem } from '@/types/superadmin.types';
import apiClient from '@/services/api/client';
import UploadTemplateModal from './components/UploadTemplateModal';
import EditTemplateModal from './components/EditTemplateModal';
import ReUploadJrxmlModal from './components/ReUploadJrxmlModal';

// ── Authenticated preview image component ──────────────────────

function AuthPreviewImage({ path, alt }: { path: string; alt: string }) {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    apiClient.get(path, { responseType: 'blob' })
      .then(response => {
        if (!cancelled) {
          objectUrl = URL.createObjectURL(response.data);
          setSrc(objectUrl);
        }
      })
      .catch(() => {
        if (!cancelled) setSrc(null);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);

  if (!src) return <div className="tmpl-preview-placeholder"><FileCode size={32} /></div>;
  return <img src={src} alt={alt} loading="lazy" />;
}

// ── Main page ──────────────────────────────────────────────────

export default function TemplateCatalogPage() {
  const { t } = useTranslation('platform');
  const { data: templates, isLoading } = useTemplateCatalog();
  const deleteMutation = useDeleteTemplate();
  const regenerateMutation = useRegeneratePreview();
  const updateMetaMutation = useUpdateTemplateMetadata();
  const { success, error: showError } = useNotification();

  const [uploadOpen, setUploadOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState<TemplateCatalogItem | null>(null);
  const [reuploadTemplate, setReuploadTemplate] = useState<TemplateCatalogItem | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const { standard, premium } = useMemo(() => {
    if (!templates) return { standard: [], premium: [] };
    const sorted = [...templates].sort((a, b) => a.sortOrder - b.sortOrder);
    return {
      standard: sorted.filter(t => !t.isPremium),
      premium: sorted.filter(t => t.isPremium),
    };
  }, [templates]);

  const nextSortOrder = useMemo(() => {
    if (!templates || templates.length === 0) return 1;
    return Math.max(...templates.map(t => t.sortOrder)) + 1;
  }, [templates]);

  const handleDelete = async (template: TemplateCatalogItem) => {
    if (template.isBuiltin) {
      showError(t('templateCatalog.delete.cannotDeleteBuiltin'));
      return;
    }
    if (!window.confirm(t('templateCatalog.delete.confirmMessage', { name: template.name }))) return;

    try {
      await deleteMutation.mutateAsync(template.id);
      success(t('templateCatalog.delete.success'));
    } catch (err: any) {
      showError(err?.message || t('templateCatalog.delete.error'));
    }
  };

  const handleRegenerate = async (template: TemplateCatalogItem) => {
    try {
      await regenerateMutation.mutateAsync(template.id);
      success(t('templateCatalog.preview.regenerated'));
    } catch (err: any) {
      showError(err?.message || t('templateCatalog.preview.regenerateError'));
    }
  };

  const handleTogglePremium = async (template: TemplateCatalogItem) => {
    try {
      await updateMetaMutation.mutateAsync({
        id: template.id,
        data: { isPremium: !template.isPremium },
      });
      success(t(template.isPremium
        ? 'templateCatalog.actions.markedStandard'
        : 'templateCatalog.actions.markedPremium'
      ));
    } catch (err: any) {
      showError(err?.message || t('templateCatalog.edit.error'));
    }
  };

  const handleDownloadJrxml = async (template: TemplateCatalogItem) => {
    try {
      const response = await apiClient.get(getDownloadJrxmlUrl(template.id), {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${template.code}.jrxml`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch {
      showError(t('templateCatalog.actions.downloadError'));
    }
  };

  const renderCard = (template: TemplateCatalogItem) => {
    const previewPath = getPreviewUrl(template.id);
    const isMenuOpen = openMenuId === template.id;

    return (
      <div key={template.id} className={`tmpl-card ${!template.isActive ? 'tmpl-inactive' : ''}`}>
        {/* Preview */}
        <div className="tmpl-preview">
          <AuthPreviewImage path={previewPath} alt={template.name} />
          {!template.isActive && (
            <div className="tmpl-inactive-badge">
              <EyeOff size={12} /> {t('templateCatalog.badges.inactive')}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="tmpl-info">
          <div className="tmpl-title-row">
            <h3 className="tmpl-name">{template.name}</h3>
            <div className="tmpl-badges">
              {template.isPremium && (
                <span className="badge badge-premium"><Crown size={10} /> {t('templateCatalog.badges.premium')}</span>
              )}
              {template.isBuiltin && (
                <span className="badge badge-builtin"><Lock size={10} /> {t('templateCatalog.badges.builtin')}</span>
              )}
              {template.hasLetterhead && (
                <span className="badge badge-letterhead">{t('templateCatalog.badges.letterhead')}</span>
              )}
            </div>
          </div>
          <p className="tmpl-desc">{template.description}</p>
          <div className="tmpl-meta">
            <span className="tmpl-code">{template.code}</span>
            <span className="tmpl-version">v{template.jrxmlVersion}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="tmpl-actions">
          <div className="tmpl-menu-wrapper">
            <button
              className="tmpl-menu-btn"
              onClick={() => setOpenMenuId(isMenuOpen ? null : template.id)}
            >
              <MoreVertical size={16} />
            </button>
            {isMenuOpen && (
              <>
                <div className="tmpl-menu-overlay" onClick={() => setOpenMenuId(null)} />
                <div className="tmpl-menu">
                  <button onClick={() => { setEditTemplate(template); setOpenMenuId(null); }}>
                    <Edit2 size={14} /> {t('templateCatalog.actions.edit')}
                  </button>
                  <button onClick={() => { handleTogglePremium(template); setOpenMenuId(null); }}>
                    {template.isPremium ? (
                      <><Star size={14} /> {t('templateCatalog.actions.markStandard')}</>
                    ) : (
                      <><Crown size={14} /> {t('templateCatalog.actions.markPremium')}</>
                    )}
                  </button>
                  <button onClick={() => { setReuploadTemplate(template); setOpenMenuId(null); }}>
                    <FileCode size={14} /> {t('templateCatalog.actions.reuploadJrxml')}
                  </button>
                  <button onClick={() => { handleRegenerate(template); setOpenMenuId(null); }}>
                    <RefreshCw size={14} /> {t('templateCatalog.actions.regeneratePreview')}
                  </button>
                  <button onClick={() => { handleDownloadJrxml(template); setOpenMenuId(null); }}>
                    <Download size={14} /> {t('templateCatalog.actions.downloadJrxml')}
                  </button>
                  {!template.isBuiltin && (
                    <button
                      className="tmpl-menu-danger"
                      onClick={() => { handleDelete(template); setOpenMenuId(null); }}
                    >
                      <Trash2 size={14} /> {t('templateCatalog.actions.delete')}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>{t('templateCatalog.title')}</h1>
          <p className="page-subtitle">{t('templateCatalog.subtitle')}</p>
        </div>
        <button className="upload-btn" onClick={() => setUploadOpen(true)}>
          <Upload size={14} />
          {t('templateCatalog.upload.button')}
        </button>
      </div>

      {isLoading ? (
        <div className="loading-state">{t('common.loading')}</div>
      ) : (
        <>
          {/* Premium Section */}
          {premium.length > 0 && (
            <section className="tmpl-section">
              <h2 className="section-title">
                <Crown size={16} /> {t('templateCatalog.sections.premium')}
                <span className="section-count">{premium.length}</span>
              </h2>
              <div className="tmpl-grid">{premium.map(renderCard)}</div>
            </section>
          )}

          {/* Standard Section */}
          <section className="tmpl-section">
            <h2 className="section-title">
              <Eye size={16} /> {t('templateCatalog.sections.standard')}
              <span className="section-count">{standard.length}</span>
            </h2>
            <div className="tmpl-grid">{standard.map(renderCard)}</div>
          </section>

          {templates?.length === 0 && (
            <div className="empty-state">
              <FileCode size={48} />
              <p>{t('templateCatalog.empty')}</p>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      <UploadTemplateModal
        isOpen={uploadOpen}
        onClose={() => setUploadOpen(false)}
        nextSortOrder={nextSortOrder}
      />
      <EditTemplateModal
        isOpen={!!editTemplate}
        onClose={() => setEditTemplate(null)}
        template={editTemplate}
      />
      <ReUploadJrxmlModal
        isOpen={!!reuploadTemplate}
        onClose={() => setReuploadTemplate(null)}
        templateId={reuploadTemplate?.id || null}
        templateCode={reuploadTemplate?.code || ''}
      />

      <style>{pageStyles}</style>
    </div>
  );
}

const pageStyles = `
  .page-container {
    padding: 24px;
    max-width: 1200px;
  }

  .page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 24px;
  }

  .page-header h1 {
    font-size: 20px;
    font-weight: 600;
    color: var(--color-text-primary);
    margin: 0;
  }

  .page-subtitle {
    font-size: 13px;
    color: var(--color-text-muted);
    margin: 4px 0 0;
  }

  .upload-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: var(--color-brand);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: opacity 0.15s;
    white-space: nowrap;
  }
  .upload-btn:hover { opacity: 0.9; }

  .tmpl-section {
    margin-bottom: 32px;
  }

  .section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: var(--color-text-secondary);
    margin: 0 0 16px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .section-count {
    background: var(--color-bg-hover);
    padding: 2px 8px;
    border-radius: 99px;
    font-size: 11px;
    font-weight: 700;
    color: var(--color-text-muted);
  }

  .tmpl-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 16px;
  }

  .tmpl-card {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 12px;
    overflow: hidden;
    transition: all 0.2s ease;
    position: relative;
  }
  .tmpl-card:hover {
    border-color: var(--color-border-default);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
  .tmpl-card.tmpl-inactive {
    opacity: 0.6;
  }

  .tmpl-preview {
    position: relative;
    height: 200px;
    background: var(--color-bg-primary);
    overflow: hidden;
  }
  .tmpl-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top;
  }

  .tmpl-preview-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-text-muted);
    opacity: 0.4;
  }

  .tmpl-inactive-badge {
    position: absolute;
    top: 8px;
    right: 8px;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px;
    background: rgba(0, 0, 0, 0.7);
    color: #fff;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
  }

  .tmpl-info {
    padding: 14px 16px;
  }

  .tmpl-title-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 6px;
  }

  .tmpl-name {
    font-size: 14px;
    font-weight: 600;
    color: var(--color-text-primary);
    margin: 0;
  }

  .tmpl-badges {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
  }

  .badge {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
    white-space: nowrap;
  }

  .badge-premium {
    background: rgba(217, 119, 6, 0.15);
    color: #D97706;
  }

  .badge-builtin {
    background: rgba(99, 102, 241, 0.15);
    color: #6366F1;
  }

  .badge-letterhead {
    background: rgba(16, 185, 129, 0.15);
    color: #10B981;
  }

  .tmpl-desc {
    font-size: 12px;
    color: var(--color-text-muted);
    margin: 0 0 8px;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .tmpl-meta {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .tmpl-code {
    font-size: 11px;
    font-family: monospace;
    color: var(--color-text-muted);
    background: var(--color-bg-primary);
    padding: 2px 6px;
    border-radius: 4px;
  }

  .tmpl-version {
    font-size: 11px;
    color: var(--color-text-muted);
  }

  .tmpl-actions {
    position: absolute;
    top: 8px;
    left: 8px;
  }

  .tmpl-menu-wrapper {
    position: relative;
  }

  .tmpl-menu-btn {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.5);
    border: none;
    border-radius: 6px;
    color: white;
    cursor: pointer;
    transition: background 0.15s;
  }
  .tmpl-menu-btn:hover {
    background: rgba(0, 0, 0, 0.7);
  }

  .tmpl-menu-overlay {
    position: fixed;
    inset: 0;
    z-index: 99;
  }

  .tmpl-menu {
    position: absolute;
    top: 32px;
    left: 0;
    min-width: 200px;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-default);
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    z-index: 100;
    padding: 4px;
  }

  .tmpl-menu button,
  .tmpl-menu a {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 10px;
    background: none;
    border: none;
    border-radius: 6px;
    color: var(--color-text-secondary);
    font-size: 12px;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.1s;
  }
  .tmpl-menu button:hover,
  .tmpl-menu a:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }

  .tmpl-menu-danger {
    color: var(--color-error, #DC2626) !important;
  }
  .tmpl-menu-danger:hover {
    background: rgba(220, 38, 38, 0.1) !important;
    color: var(--color-error, #DC2626) !important;
  }

  .loading-state,
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px;
    color: var(--color-text-muted);
    font-size: 14px;
    gap: 12px;
  }

  @media (max-width: 640px) {
    .page-header {
      flex-direction: column;
      gap: 12px;
    }
    .tmpl-grid {
      grid-template-columns: 1fr;
    }
  }
`;
